import React, { createContext, useContext, useReducer, JSX } from 'react';

export enum ActionType {
  ADD = 'ADD',
  UPDATE = 'UPDATE',
  REMOVE = 'REMOVE',
  REMOVE_ALL = 'REMOVE_ALL'
}

export enum AlertType {
  SUCCESS = 'SUCCESS',
  DANGER = 'DANGER',
  WARNING = 'WARNING'
}

export enum AlertPositionType {
  TOP = 'TOP',
  BOTTOM = 'BOTTOM',
  CENTER = 'CENTER'
}

export interface AlertMessageType {
  type: AlertType;
  message: string;
  id?: number;
  title?: string;
  position?: AlertPositionType;
  hideAfterTimeOut?: number;
}

type Action =
  | { type: ActionType.ADD; payload: AlertMessageType }
  | { type: ActionType.UPDATE; payload: Partial<AlertMessageType>; id: number }
  | { type: ActionType.REMOVE_ALL }
  | { type: ActionType.REMOVE; payload: number };

interface State {
  alerts: AlertMessageType[];
}

export const reducer = (state: State, action: Action): State => {
  try {
    switch (action.type) {
      case ActionType.ADD:
        const newAlert = action.payload.id
          ? action.payload
          : { ...action.payload, id: state.alerts.length + 1 };
        return {
          ...state,
          alerts: state.alerts.concat([newAlert])
        };

      case ActionType.UPDATE:
        const { id } = action;
        let index: null | number = null;

        state.alerts.map((alert, ind) => {
          if (alert?.id === id) {
            index = ind;
          }
        });

        if (index === null) return state;

        return {
          ...state,
          alerts: [
            ...state.alerts.slice(0, index),
            { ...state.alerts[index], ...action.payload },
            ...state.alerts.slice(index + 1)
          ]
        };

      case ActionType.REMOVE:
        return {
          ...state,
          alerts: state.alerts.filter(alert => {
            if (alert.id !== action.payload) {
              return alert;
            }
          })
        };

      case ActionType.REMOVE_ALL:
        return {
          ...state,
          alerts: []
        };

      default:
        return state;
    }
  } catch (error) {
    return state;
  }
};

/**
 *   Alert context
 *
 */

type AlertContextProps = {
  alerts: State['alerts'];
  addAlert: (a: AlertMessageType) => void;
  updateAlert: (a: number, b: Partial<AlertMessageType>) => void;
  removeAlert: (a: number) => void;
  removeAllAlerts: () => void;
};

const initialState: AlertContextProps = {
  alerts: [],
  addAlert: () => null,
  updateAlert: () => null,
  removeAlert: () => null,
  removeAllAlerts: () => null
};

const AlertContext = createContext<AlertContextProps>(initialState);

export const useAlertsContext = (): AlertContextProps => {
  return useContext(AlertContext);
};

const init = (data: State): State => {
  return data;
};

export const AlertDataProvider = ({ children }: { children: JSX.Element }) => {
  const [state, dispatch] = useReducer(reducer, { alerts: [] }, init);

  const addAlert = (a: AlertMessageType) => {
    dispatch({
      type: ActionType.ADD,
      payload: a
    });
  };

  const updateAlert = (a: number, b: Partial<AlertMessageType>) => {
    dispatch({
      type: ActionType.UPDATE,
      payload: b,
      id: a
    });
  };

  const removeAlert = (a: number) => {
    dispatch({
      type: ActionType.REMOVE,
      payload: a
    });
  };

  const removeAllAlerts = () => {
    dispatch({
      type: ActionType.REMOVE_ALL
    });
  };

  return (
    <AlertContext.Provider
      value={{
        alerts: state.alerts,
        addAlert,
        updateAlert,
        removeAlert,
        removeAllAlerts
      }}
    >
      {children}
    </AlertContext.Provider>
  );
};
